import React, { useEffect, useState } from "react";
import iconEdit from "@/assets/images/iconEdit.png";
import DashboardHeader from "@/components/DashboardHeader";
import SelectInput from "@/components/selectInput/SelectInput";
import { SubmitHandler, useForm } from "react-hook-form";
import {
    AstericSign,
    FormHookInput,
    Input,
    Loader,
    Button,
    emailValidator,
    namePattern,
    NON_DIGIT_REGEX,
    STARTS_WITH_OPTIONAL_1_REGEX,
} from "@thdinc/hpf-shared";
import userDefaultImg from "@/assets/images/defaultuser.jpg";
import { useNavigate } from "react-router";
import Alert from "@/components/alert/Alert";
import { PATCH_MULTIPART, POST_MULTIPART } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { useAppSelector } from "@/hooks/hook";
import {
    ACCEPTED_IMAGE_TYPES,
    genderData,
    MAX_FILE_SIZE_MB,
} from "@/constants/Common";
import useLoading from "@/hooks/useLoading";
import useError from "@/hooks/useError ";

interface customBartender {
    isAdmin?: boolean;
    businessId?: string;
    next?: () => void;
    prev?: () => void;
    closeModal: () => void;
    rerenderFun?: () => void;
    isEdit?: boolean;
    receivedData?: any;
    /** When creating from event detail, pass booking so the new bartender is associated with this booking */
    booking?: { id: string } | null;
}

type Inputs = {
    name: string;
    lastName: string;
    email: string;
    phone_number: string;
    fee: number;
};
const CustomBartenderModal: React.FC<customBartender> = ({
    isAdmin,
    businessId,
    next,
    prev,
    isEdit,
    receivedData,
    closeModal,
    rerenderFun,
    booking,
}) => {
    const { customerBartenders } = API_ROUTES;
    const { refreshToken } = useAppSelector((store) => store.user);
    const { isLoading, startLoading, stopLoading } = useLoading();
    const [error, setCustomError] = useError();
    const LoggedInBusinessId =
        useAppSelector((store) => store?.user?.business_id) || "";

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<Inputs>();
    // const [imageUrl, setImageUrl] = useState<File | null>(null);
    const [profile, setProfile] = useState<File | null>(null);
    const [isBartender, setIsBartender] = useState<boolean>(false);
    const [errorState, setErrorState] = useState<{
        name: string | null;
        error: string | null;
    }>({ name: "", error: "" });
    const [selectedGender, setSelectedGender] = useState<string>(
        receivedData?.gender ?? "null"
    );
    const [selectLang, setSelectLang] = useState<string[]>(
        receivedData?.languages ?? []
    );
    const navigate = useNavigate();

    const [modal, setModal] = useState<boolean>(false);
    const [activeData, setActiveData] = useState<any | null>(null);

    useEffect(() => {
        if (isEdit) {
            // set states if edit mode is enabled
            setValue("name", receivedData?.first_name);
            setValue("lastName", receivedData?.last_name);
            setValue("email", receivedData?.email);
            setValue("phone_number", receivedData?.phone_number);
            setSelectLang(receivedData?.languages);
            setSelectedGender(receivedData?.gender);
        }
    }, []);

    const selectGenderFun = (gender: string) => {
        setSelectedGender(gender);
        if (errorState.name === "gender") {
            setErrorState({ name: null, error: null });
        }
    };

    const selectLangFun = (checked: boolean, lang: string) => {
        if (checked) {
            setSelectLang([...selectLang, lang]);
            setErrorState({ name: null, error: null });
        } else {
            setSelectLang(selectLang.filter((item) => item !== lang));
        }
    };

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
        try {
            // if provided conditions are failed through an error
            if (
                (profile && selectedGender !== "null" && selectLang.length !== 0) ||
                (isEdit && selectedGender !== "null" && selectLang.length !== 0)
            ) {
                startLoading();
                const formData = new FormData();
                formData.append("first_name", data.name);
                formData.append("last_name", data.lastName);
                formData.append("gender", selectedGender);
                formData.append("email", data.email);
                formData.append("phone_number", data.phone_number);
                formData.append("language", JSON.stringify(selectLang));

                if (isAdmin && businessId) {
                    formData.append("isAdmin", isAdmin.toString());
                    formData.append("business_id", businessId);
                    formData.append("is_bartender", isBartender.toString());
                } else {
                    formData.append("business_id", LoggedInBusinessId);
                }
                if (booking?.id) {
                    formData.append("booking_id", booking.id);
                }
                //formData.append("location", selectLocation);
                // formData.append("hourly_rate", String(data.fee));
                // formData.append("guest_capacity", guests);
                if (profile) {
                    formData.append("avatar", profile);
                }
                // if (imageUrl) {
                //   formData.append("other_photos", imageUrl);
                // }
                setActiveData(formData);
                // setModal(true);
                //  await saveDetails()

                const response = isEdit
                    ? await PATCH_MULTIPART(
                        customerBartenders + "/" + receivedData?.id,
                        formData,
                        refreshToken
                    )
                    : await POST_MULTIPART(customerBartenders, formData, refreshToken);

                if (response.data.success) {
                    rerenderFun && rerenderFun();
                    closeModal();
                    Alert({ message: response?.data?.message });
                } else {
                    Alert({ type: "error", message: response?.data?.message });
                }
            }
            if (!profile && !isEdit) {
                setErrorState({ name: "dp", error: "Please upload profile image" });
            } else if (selectedGender === "null") {
                setErrorState({ name: "gender", error: "Please select a gender" });
            } else if (selectLang.length === 0) {
                setErrorState({ name: "lang", error: "Please select a language" });
            }
        } catch (error) {
            console.log(error);
        } finally {
            stopLoading();
        }
    };
    // const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
    const handleProfileImg = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                if (file.size <= MAX_FILE_SIZE_MB * 1024 * 1024) {
                    // Convert MB to bytes
                    const fileWithTimestamp = addTimestampToFile(file);
                    setProfile(fileWithTimestamp); // Set profile image with timestamped file name
                    setErrorState({ name: null, error: null });
                } else {
                    // if the image selected with wrong size
                    setErrorState({
                        name: "dp",
                        error: `File size exceeds ${MAX_FILE_SIZE_MB} MB. Please choose a smaller image.`,
                    });
                }
            } else {
                // if the image selected with wrong type
                setErrorState({
                    name: "dp",
                    error:
                        "Please select a valid image file (JPEG, PNG, GIF, BMP, or TIFF).",
                });
            }
        }
    };
    function addTimestampToFile(file: File): File {
        // Create a new file object with timestamped name
        const timestamp = Date.now(); // Get current timestamp
        const fileName = `${timestamp}_${file.name}`; // Add timestamp to file name
        return new File([file], fileName, { type: file.type }); // Create a new file object with timestamped name
    }


    return (
        <div className="w-full ">
            <form
                onSubmit={handleSubmit(onSubmit)}
                className=" mx-auto  md:gap-4      "
            >
                {/* left div */}
                <div className="bg-white rounded-2xl   w-full  flex flex-col items-center gap-3  ">
                    <div className="flex flex-col  items-center  ">
                        <div className="justify-center relative flex ">
                            <img
                                className="w-32 h-32 object-cover rounded-full   border-2 self-center"
                                src={
                                    profile
                                        ? URL.createObjectURL(profile)
                                        : receivedData?.avatar
                                            ? receivedData?.avatar
                                            : userDefaultImg
                                }
                            />
                            {/* <div className=" font-semibold my-2">*</div> */}
                            <div className="bg-primary-red absolute rounded-full p-2 bottom-0 ml-24  cursor-pointer">
                                <label className=" cursor-pointer">
                                    <img className="h-3 w-auto" src={iconEdit} />
                                    <input
                                        accept="image/*"
                                        id="profile-img"
                                        onChange={handleProfileImg}
                                        type="file"
                                        className="hidden"
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="text-center">
                            {errorState.name === "dp" && (
                                <p className="text-red-500 text-xs mt-1 py-">
                                    {errorState.error}
                                </p>
                            )}
                            <p className="text-gray-500 text-xs mt-2 md:w-8/12 mx-auto ">
                                Please upload an image in one of the accepted formats: JPEG, PNG
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-6 items-center justify-center mt-4 w-full">
                        <div className="flex gap-4 flex-col md:flex-row w-full  ">
                            <div className="w-full ">
                                <div className=" my-2 ml-4 text-sm font-semibold">
                                    First Name <span className="text-primary-red">*</span>{" "}
                                </div>
                                <FormHookInput
                                    type="text"
                                    bg={"bg-p-gray"}
                                    border="none"
                                    placeholder="First Name"
                                    rounded="rounded-full"
                                    padding="px-5 py-3"
                                    register={register("name", {
                                        required: "First Name is required",
                                        maxLength: {
                                            value: 50,
                                            message:
                                                "First Name length can't be more than 50 characters.",
                                        },
                                        pattern: {
                                            value: namePattern, // allow letters, hyphens, and apostrophes, between 1 and 50 characters
                                            message: "Invalid first name format",
                                        },
                                    })}
                                    error={errors.name}
                                />
                            </div>
                            <div className="w-full ">
                                <div className=" my-2 ml-4 text-sm font-semibold">
                                    Last Name <span className="text-primary-red">*</span>{" "}
                                </div>
                                <FormHookInput
                                    bg={"bg-p-gray"}
                                    border="none"
                                    type="text"
                                    padding="px-5 py-3"
                                    rounded="rounded-full"
                                    placeholder="Last Name"
                                    register={register("lastName", {
                                        required: "Last First Name is required",
                                        maxLength: {
                                            value: 50,
                                            message:
                                                "Last Name length can't be more than 50 characters.",
                                        },

                                        pattern: {
                                            value: namePattern, // allow letters, hyphens, and apostrophes, between 1 and 50 characters
                                            message: "Invalid Last Name format",
                                        },
                                    })}
                                    error={errors.lastName}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4 w-full flex-col md:flex-row">
                            <div className="w-full ">
                                <div className=" my-2 ml-4 text-sm font-semibold">
                                    Email <span className="text-primary-red">*</span>{" "}
                                </div>
                                <FormHookInput
                                    rounded="rounded-full"
                                    padding="px-5 py-3"
                                    bg={"bg-p-gray"}
                                    border="none"
                                    type="email"
                                    placeholder="Email"
                                    register={register("email", {
                                        required: "Email is required!",
                                        pattern: emailValidator,
                                    })}
                                    error={errors.email}
                                />
                            </div>
                            <div className="w-full ">
                                <div className=" my-2 ml-4 text-sm font-semibold">
                                    Phone
                                </div>
                                <FormHookInput
                                    type="text"
                                    placeholder="Phone Number"
                                    register={register("phone_number", {
                                        required: false,
                                        onChange: (e) => {
                                            let { value } = e.target;
                                            // Remove all non-digit characters except the initial "+1 "
                                            value = value
                                                .replace(NON_DIGIT_REGEX, "")
                                                .replace(STARTS_WITH_OPTIONAL_1_REGEX, "1"); // Ensures it starts with "1"
                                            value = `+1 ${value.substring(1, 4)} ${value.substring(
                                                4,
                                                7
                                            )} ${value.substring(7, 11)}`.trim();
                                            e.target.value = value;
                                        },
                                    })}
                                    error={errors.phone_number}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4 w-full flex-col md:flex-row">
                            <div className="w-full ">
                                <div className=" my-2 ml-4 text-sm font-semibold">
                                    Gender <span className="text-primary-red">*</span>{" "}
                                </div>
                                <SelectInput
                                    bg={"bg-p-gray"}
                                    border="none"
                                    disabled={false}
                                    options={genderData}
                                    value={selectedGender}
                                    onChange={selectGenderFun}
                                    classes="py-2 "
                                    rounded="rounded-full"
                                    padding="px-5 py-3"
                                />
                                {errorState.name === "gender" && (
                                    <p className="text-red-500 text-xs mt-1 py-">
                                        {errorState.error}
                                    </p>
                                )}
                            </div>

                            <div className="w-full  ml-4 flex flex-col  ">
                                <div className=" my-2 text-sm font-semibold">
                                    Language <span className="text-primary-red">*</span>{" "}
                                </div>
                                <div className="flex justify-start mt-2 gap-5">
                                    <div
                                        className={`flex items-center gap-2 ${selectLang.includes("en") && "bg-[#ffeee5] "
                                            } px-3 py-2 rounded-full`}
                                    >
                                        <Input
                                            onChange={(e) => selectLangFun(e.target.checked, "en")}
                                            // className="w-4 h-4 cursor-pointer accent-primary-red"
                                            className="cursor-pointer appearance-none h-5 w-5 border border-gray-300 rounded-full checked:bg-primary-red checked:border-transparent focus:outline-none"
                                            type="checkbox"
                                            checked={selectLang.includes("en")}
                                        />
                                        <p>English</p>
                                    </div>
                                    <div
                                        className={`flex items-center gap-2 ${selectLang.includes("es") && "bg-[#ffeee5] "} px-3 py-2 rounded-full`}>
                                        <Input
                                            onChange={(e) => selectLangFun(e.target.checked, "es")}
                                            checked={selectLang.includes("es")}
                                            // className="w-4 h-4 cursor-pointer accent-primary-red"
                                            className="cursor-pointer appearance-none h-5 w-5 border border-gray-300 rounded-full checked:bg-primary-red checked:border-transparent focus:outline-none"
                                            type="checkbox"
                                        />
                                        <p>Spanish</p>
                                    </div>
                                </div>
                                {errorState.name === "lang" && (
                                    <p className="text-red-500 text-xs mt-1 py-">
                                        {errorState.error}
                                    </p>
                                )}
                            </div>
                        </div>
                        {isAdmin && (
                            <div className="w-full mt-2">
                                <div className="flex items-center gap-2">
                                    <p>Is Bartender</p>
                                    <Input
                                        onChange={(e) => setIsBartender(e.target.checked)}
                                        checked={isBartender}
                                        className="w-4 h-4 cursor-pointer accent-primary-red"
                                        type="checkbox"
                                    />
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end mt-5 w-full gap-4">
                            <Button
                                classes={
                                    "bg-primary-red text-white hover:bg-primary-red-button-hover  mb-10 "
                                }
                                type="submit"
                                disabled={isLoading}
                            >
                                {" "}
                                {isLoading ? <Loader color="bg-gray-50" /> : "Save"}
                            </Button>
                        </div>
                        {error?.name === "add" && (
                            <p className="text-primary-red text-center mt-">
                                {error.message}
                            </p>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CustomBartenderModal;
